import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, Upload, Search, Edit, Trash2, Package, Download, AlertTriangle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import AddProductDialog from "../components/AddProductDialog";
import { toast } from "sonner";

export default function Products() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterMode, setFilterMode] = useState("all"); // "all" | "unsold"
  const [unsoldMonths, setUnsoldMonths] = useState(2);
  const [unsoldMeta, setUnsoldMeta] = useState({ count: 0, cutoff: "" });
  const [selected, setSelected] = useState(new Set()); // bulk-delete
  const [purgeStep, setPurgeStep] = useState(0); // 0=closed, 1=warn, 2=password
  const [purgePwd, setPurgePwd] = useState("");
  const [purgeConfirm, setPurgeConfirm] = useState("");

  const load = async () => {
    if (filterMode === "unsold") {
      const r = await api.get(`/products/unsold?months=${unsoldMonths}`);
      setProducts(r.data.products || []);
      setUnsoldMeta({ count: r.data.count, cutoff: r.data.cutoff });
      setSelected(new Set());
    } else {
      const r = await api.get("/products");
      setProducts(r.data);
      setSelected(new Set());
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterMode, unsoldMonths]);

  const bulkDelete = async () => {
    if (selected.size === 0) { toast.error("Select at least one product"); return; }
    if (!window.confirm(`Delete ${selected.size} unsold product(s)? This cannot be undone.`)) return;
    let done = 0, failed = 0;
    for (const id of selected) {
      try { await api.delete(`/products/${id}`); done++; } catch { failed++; }
    }
    toast.success(`Deleted ${done}${failed ? ` · ${failed} failed` : ""}`);
    load();
  };

  const purgeAll = async () => {
    try {
      const r = await api.post("/products/delete-all", { password: purgePwd, confirm: purgeConfirm });
      toast.success(`Deleted ALL ${r.data.deleted} products`);
      setPurgeStep(0); setPurgePwd(""); setPurgeConfirm("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.inserted} products`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { e.target.value = ""; }
  };

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    load();
    toast.success("Deleted");
  };

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q);
  });

  const isAdmin = user?.role === "admin";

  return (
    <div className="p-8 overflow-y-auto" data-testid="products-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-overline text-muted-foreground">Inventory</div>
          <h1 className="font-heading text-4xl font-bold text-ink">Products</h1>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button
                variant="outline"
                className="gap-2"
                data-testid="export-products-btn"
                onClick={async () => {
                  try {
                    const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
                    const resp = await fetch(`${API}/products/export`, { credentials: "include" });
                    if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
                    const blob = await resp.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    const cd = resp.headers.get("content-disposition") || "";
                    const match = cd.match(/filename="?([^";]+)"?/);
                    a.href = url;
                    a.download = match ? match[1] : "products_export.xlsx";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    (await import("sonner")).toast.success("Exported");
                  } catch (e) {
                    (await import("sonner")).toast.error(e.message || "Export failed");
                  }
                }}
              >
                <Download size={14}/>Export Excel
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                data-testid="bulk-stock-btn"
                onClick={async () => {
                  if (!window.confirm("Set ALL products' stock to 40 (soaps 100) in shop1 & shop2? Existing stock will be OVERWRITTEN.")) return;
                  try {
                    const r = await api.post("/products/bulk-stock-reset", { default_qty: 40, soap_qty: 100, shops: ["shop1", "shop2"] });
                    (await import("sonner")).toast.success(`Stock reset: ${r.data.updated_default} at 40, ${r.data.updated_soap} soaps at 100`);
                    load();
                  } catch (e) {
                    (await import("sonner")).toast.error(e?.response?.data?.detail || "Bulk reset failed");
                  }
                }}
              >
                <Package size={14}/>Reset Stock (40/100)
              </Button>
              <Button
                variant="outline"
                className="gap-2 border-destructive text-destructive hover:bg-destructive/10"
                data-testid="delete-zero-stock-btn"
                onClick={async () => {
                  const zeroCount = products.filter((p) => (p.stock?.shop1 || 0) <= 0 && (p.stock?.shop2 || 0) <= 0).length;
                  if (zeroCount === 0) { toast.error("No products with 0 stock in both shops"); return; }
                  if (!window.confirm(`Delete ${zeroCount} products with 0 stock in BOTH shops? Cannot be undone.`)) return;
                  try {
                    const r = await api.post("/products/delete-zero-stock", {});
                    toast.success(`Deleted ${r.data.deleted} zero-stock products`);
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed");
                  }
                }}
              >
                <Trash2 size={14}/>Delete Zero-Stock
              </Button>
              <Button
                variant="destructive"
                className="gap-2"
                data-testid="delete-all-btn"
                onClick={() => { setPurgeStep(1); setPurgePwd(""); setPurgeConfirm(""); }}
              >
                <AlertTriangle size={14}/>Delete All
              </Button>
              <label className="cursor-pointer">
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} data-testid="excel-import" />
                <Button asChild variant="outline" className="gap-2"><span><Upload size={14}/>Import Excel</span></Button>
              </label>
              <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-product-btn" className="bg-terracotta hover:bg-terracotta-hover text-white gap-2">
                <Plus size={14}/>New Product
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="products-search" placeholder="Search by name or barcode" className="pl-9" value={search} onChange={(e)=>setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1" data-testid="filter-switch">
          <button
            onClick={() => setFilterMode("all")}
            className={`px-3 py-2 rounded text-sm font-medium ${filterMode === "all" ? "bg-terracotta text-white" : "bg-surface-alt"}`}
            data-testid="filter-all"
          >All Products</button>
          <button
            onClick={() => setFilterMode("unsold")}
            className={`px-3 py-2 rounded text-sm font-medium ${filterMode === "unsold" ? "bg-terracotta text-white" : "bg-surface-alt"}`}
            data-testid="filter-unsold"
          >Not Sold In</button>
          {filterMode === "unsold" && (
            <>
              <select
                value={unsoldMonths}
                onChange={(e) => setUnsoldMonths(Number(e.target.value))}
                className="h-9 px-2 border border-border rounded text-sm bg-white"
                data-testid="unsold-months"
              >
                <option value={1}>1 month</option>
                <option value={2}>2 months</option>
                <option value={3}>3 months</option>
                <option value={6}>6 months</option>
                <option value={12}>1 year</option>
              </select>
              <span className="self-center text-xs text-muted-foreground ml-1">
                {unsoldMeta.count} dead-stock products
              </span>
            </>
          )}
        </div>
        {filterMode === "unsold" && isAdmin && selected.size > 0 && (
          <Button
            variant="destructive"
            onClick={bulkDelete}
            data-testid="bulk-delete-btn"
            className="gap-2 ml-auto"
          >
            <Trash2 size={14}/>Delete {selected.size} Selected
          </Button>
        )}
      </div>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface-alt">
              {filterMode === "unsold" && isAdmin && (
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === filtered.length}
                    onChange={(e) => setSelected(e.target.checked ? new Set(filtered.map((p) => p.product_id)) : new Set())}
                    data-testid="select-all"
                  />
                </TableHead>
              )}
              <TableHead className="w-12"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Barcode</TableHead>
              <TableHead className="text-right">MRP</TableHead>
              <TableHead className="text-right">Retail</TableHead>
              <TableHead className="text-right">Shop1</TableHead>
              <TableHead className="text-right">Shop2</TableHead>
              {filterMode === "unsold" ? (
                <TableHead>Last Sold</TableHead>
              ) : (
                <TableHead>Expiry (S1/S2)</TableHead>
              )}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                {filterMode === "unsold" && isAdmin && (
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selected.has(p.product_id)}
                      onChange={(e) => {
                        const n = new Set(selected);
                        if (e.target.checked) n.add(p.product_id); else n.delete(p.product_id);
                        setSelected(n);
                      }}
                      data-testid={`select-${p.product_id}`}
                    />
                  </TableCell>
                )}
                <TableCell>
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="w-9 h-9 rounded object-cover border border-border" />
                  ) : (
                    <div className="w-9 h-9 rounded bg-sand border border-border flex items-center justify-center font-heading text-terracotta/40 font-bold">{p.name?.[0]?.toUpperCase()}</div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell><Badge variant="outline">{p.category}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{p.barcode}</TableCell>
                <TableCell className="text-right font-mono">₹{p.mrp}</TableCell>
                <TableCell className="text-right font-mono text-terracotta">₹{p.retail_price}</TableCell>
                <TableCell className={`text-right font-mono ${p.stock?.shop1 <= p.reorder_level ? "text-destructive" : ""}`}>{p.stock?.shop1 ?? 0}</TableCell>
                <TableCell className={`text-right font-mono ${p.stock?.shop2 <= p.reorder_level ? "text-destructive" : ""}`}>{p.stock?.shop2 ?? 0}</TableCell>
                {filterMode === "unsold" ? (
                  <TableCell className="text-xs">
                    {p.last_sold_at ? (
                      <span className="text-muted-foreground">{new Date(p.last_sold_at).toLocaleDateString()}</span>
                    ) : (
                      <span className="text-destructive font-bold">Never sold</span>
                    )}
                  </TableCell>
                ) : (
                  <TableCell className="text-xs">{p.expiry?.shop1?.slice(0,10) || "-"} / {p.expiry?.shop2?.slice(0,10) || "-"}</TableCell>
                )}
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-${p.product_id}`}><Edit size={14} /></Button>
                  {isAdmin && <Button size="icon" variant="ghost" onClick={() => del(p.product_id)} data-testid={`del-${p.product_id}`}><Trash2 size={14} className="text-destructive"/></Button>}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={filterMode === "unsold" && isAdmin ? 11 : 10} className="text-center text-muted-foreground py-8">
                {filterMode === "unsold" ? "🎉 No unsold products! Everything is moving." : "No products found."}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <AddProductDialog open={open} onOpenChange={setOpen} onCreated={load} product={editing} />

      {/* Purge ALL dialog — 2-step confirmation */}
      <Dialog open={purgeStep > 0} onOpenChange={(o) => { if (!o) { setPurgeStep(0); setPurgePwd(""); setPurgeConfirm(""); } }}>
        <DialogContent data-testid="purge-dialog" className="border-destructive">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-destructive">
              <AlertTriangle size={20}/>DANGER · Delete ALL Products
            </DialogTitle>
          </DialogHeader>
          {purgeStep === 1 && (
            <div className="space-y-3">
              <div className="bg-destructive/10 border border-destructive rounded p-3 text-sm space-y-2">
                <div className="font-bold text-destructive">⚠️ WARNING #1 of 2</div>
                <ul className="list-disc pl-4 text-xs space-y-1">
                  <li><b>{products.length}</b> products database se hamesha ke liye delete ho jayenge</li>
                  <li>Stock, MRP, categories, barcodes — sab kuch chala jayega</li>
                  <li>Existing bills / reports intact rahenge, but naya bill nahi ban sakta</li>
                  <li>Yeh action <b>UNDO nahi ho sakta</b>. Data recovery only via DB backup possible</li>
                </ul>
              </div>
              <div className="text-xs text-muted-foreground">
                Agar seriously delete karna hai toh <b>"I Understand, Continue"</b> dabaao.
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPurgeStep(0)} data-testid="purge-cancel-1">Cancel</Button>
                <Button variant="destructive" onClick={() => setPurgeStep(2)} data-testid="purge-continue">
                  I Understand, Continue
                </Button>
              </DialogFooter>
            </div>
          )}
          {purgeStep === 2 && (
            <div className="space-y-3">
              <div className="bg-destructive text-white rounded p-3 text-sm space-y-2">
                <div className="font-bold">⚠️ FINAL CONFIRMATION #2 of 2</div>
                <div className="text-xs">Type <code className="bg-black/30 px-1 rounded">DELETE ALL</code> and enter your admin password to proceed.</div>
              </div>
              <div>
                <label className="text-xs font-bold">Type "DELETE ALL" (case sensitive)</label>
                <Input
                  value={purgeConfirm}
                  onChange={(e) => setPurgeConfirm(e.target.value)}
                  placeholder="DELETE ALL"
                  className="mt-1 font-mono"
                  data-testid="purge-confirm-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold">Admin password</label>
                <Input
                  type="password"
                  value={purgePwd}
                  onChange={(e) => setPurgePwd(e.target.value)}
                  placeholder="Your login password"
                  className="mt-1"
                  data-testid="purge-password-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPurgeStep(0)} data-testid="purge-cancel-2">Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={purgeConfirm !== "DELETE ALL" || !purgePwd}
                  onClick={purgeAll}
                  data-testid="purge-final-btn"
                >
                  <AlertTriangle size={14} className="mr-2"/>PURGE ALL {products.length} PRODUCTS
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="mt-6 text-xs text-muted-foreground">
        <b>Excel format:</b> columns required: <code>name, mrp, retail_price, barcode, category, shop1_qty, shop2_qty, reorder_level</code>
      </div>
    </div>
  );
}
